/**
 * InboundAssistants — the review surface for the connection registry (S2).
 *
 * Every assistant that has ever opened a session against the TekStak MCP
 * (mcp.tekstak.app) appears here with what the server knows about it:
 * announced name/version, how it authenticated, the subject it authenticated
 * as, and its counters. Nothing about WHAT it did — the registry keeps
 * metadata only (boundary decision #21).
 *
 * Three verdicts, and the server honours them (registry.ts):
 *   - New          → first-ever pair; the alert already fired. Acknowledge it
 *                    or retire it. Until then its flow lane wears "new" and
 *                    the MCP holds it to READ-ONLY tools (S3 quarantine);
 *                    every write it attempts is refused and counted here.
 *   - Acknowledged → known and trusted.
 *   - Retired      → refused at initialize and before every tool call; open
 *                    sessions are cut off within a minute. Reinstate to undo.
 * "Dormant" is not a verdict — it is derived (no session in 30 days) and is
 * the nag that keeps a forgotten source from being forgotten.
 *
 * Optimistic per the TekStak standard: the pill flips on click, rolls back on
 * error, reconciles on settle; only the row being changed is pending.
 */
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, ShieldCheck, ShieldOff, RotateCcw } from 'lucide-react';
import apiClient from '../../lib/apiClient';
import { confirmDialog } from '../ConfirmDialog';
import { timeAgo } from './ConnectorRow';

const QUERY_KEY = ['connected-clients'];

const STATUS_ORDER = { unrecognised: 0, acknowledged: 1, retired: 2 };

const AUTH_LABEL = {
  oauth: 'OAuth',
  'static-token': 'Static token',
  stdio: 'Local (stdio)',
  unknown: 'Unknown auth',
};

function statusPill(c) {
  if (c.status === 'retired') return { cls: 'cx-pill-disabled', text: 'Retired' };
  if (c.status === 'unrecognised') return { cls: 'cx-pill-degraded', text: 'New — read-only until acknowledged' };
  return { cls: 'cx-pill-healthy', text: 'Acknowledged' };
}

export function sortClients(list) {
  return [...list].sort((a, b) => {
    const s = (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9);
    if (s !== 0) return s;
    return new Date(b.lastSeenAt || 0) - new Date(a.lastSeenAt || 0);
  });
}

export default function InboundAssistants() {
  const queryClient = useQueryClient();
  const [pendingId, setPendingId] = React.useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => apiClient.get('/api/v1/funnel/clients'),
    staleTime: 30_000,
  });

  const review = useMutation({
    mutationFn: ({ id, status }) => apiClient.patch(`/api/v1/funnel/clients/${id}`, { status }),
    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      const previous = queryClient.getQueryData(QUERY_KEY);
      setPendingId(id);
      queryClient.setQueryData(QUERY_KEY, (old) => old && {
        ...old,
        clients: (old.clients || []).map((c) => (c.id === id ? { ...c, status, dormant: status === 'retired' ? false : c.dormant } : c)),
      });
      return { previous };
    },
    onError: (_err, _vars, ctx) => { if (ctx?.previous) queryClient.setQueryData(QUERY_KEY, ctx.previous); },
    onSettled: () => {
      setPendingId(null);
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      // The flow view's lane badges come from the same rows.
      queryClient.invalidateQueries({ queryKey: ['flow'] });
    },
  });

  const retire = async (c) => {
    const ok = await confirmDialog({
      title: 'Retire this assistant',
      message: `Refuse "${c.label}" (${AUTH_LABEL[c.authMode] || c.authMode}, ${c.subject}) at the MCP from now on? `
        + 'Any open session is cut off within a minute. You can reinstate it here later.',
      confirmLabel: 'Retire',
      danger: true,
    });
    if (ok) review.mutate({ id: c.id, status: 'retired' });
  };

  const clients = sortClients(data?.clients || []);
  const newCount = clients.filter((c) => c.status === 'unrecognised').length;

  return (
    <section className="cx-section" aria-labelledby="cxi-title">
      <h2 className="cx-section-title" id="cxi-title">
        Inbound assistants
        {newCount > 0 && <span className="cx-pill cx-pill-degraded cxi-title-count">{newCount} to review</span>}
      </h2>
      <p className="cx-section-note">
        Every assistant that has connected to your MCP at mcp.tekstak.app, learned from its own
        sessions — nothing to register by hand. A new connection is flagged the moment it appears
        and held to read-only tools until you acknowledge it; a retired one is refused at the door.
        Metadata only: the server records who connected and how often, never what was said.
      </p>

      {isLoading && (
        <div className="cxi-empty"><Loader2 size={14} className="cx-spin" /> Loading connections…</div>
      )}
      {error && <div className="cx-error">Could not load connections: {error.message}</div>}
      {!isLoading && !error && clients.length === 0 && (
        <div className="cxi-empty">
          No assistant has connected yet. A row appears the first time a client opens a session.
        </div>
      )}

      {clients.length > 0 && (
        <div className="cxi-list" role="list">
          {clients.map((c) => {
            const pill = statusPill(c);
            const busy = pendingId === c.id;
            return (
              <div key={c.id} className={`cxi-row cxi-row--${c.status}`} role="listitem">
                <div className="cxi-main">
                  <div className="cxi-head">
                    <span className="cxi-name">{c.label}</span>
                    <span className={`cx-pill ${pill.cls}`}>{pill.text}</span>
                    {c.dormant && <span className="cx-pill cx-pill-unknown">Dormant</span>}
                  </div>
                  <div className="cxi-sub">
                    <span className="cxi-auth">{AUTH_LABEL[c.authMode] || c.authMode}</span>
                    <span className="cxi-subject" title={c.subject}>{c.subject}</span>
                    {c.clientName && (
                      <span className="cxi-announced" title="What the client announced on initialize">
                        {c.clientName}{c.clientVersion ? ` ${c.clientVersion}` : ''}
                      </span>
                    )}
                  </div>
                  <div className="cxr-meta">
                    <span className="cxr-meta-item">First seen {timeAgo(c.firstSeenAt) || '—'}</span>
                    <span className="cxr-meta-item">Last seen {timeAgo(c.lastSeenAt) || '—'}</span>
                    <span className="cxr-meta-item">{c.sessionCount} sessions</span>
                    <span className="cxr-meta-item">{c.toolCalls} calls · {c.writeCalls} writes</span>
                    {c.blockedCalls > 0 && (
                      <span className="cxr-meta-item cxi-blocked" title="Writes refused while unacknowledged">
                        {c.blockedCalls} {c.blockedCalls === 1 ? 'write' : 'writes'} blocked
                      </span>
                    )}
                    {c.lastTool && <span className="cxr-meta-item">Last tool <code>{c.lastTool}</code></span>}
                  </div>
                </div>
                <div className="cxi-actions">
                  {busy && <Loader2 size={14} className="cx-spin" aria-label="Saving" />}
                  {c.status === 'unrecognised' && (
                    <button type="button" className="cxr-btn cxr-btn--primary" disabled={busy}
                      onClick={() => review.mutate({ id: c.id, status: 'acknowledged' })}>
                      <ShieldCheck size={14} /> Acknowledge
                    </button>
                  )}
                  {c.status !== 'retired' && (
                    <button type="button" className="cxr-btn cxr-btn--danger" disabled={busy} onClick={() => retire(c)}>
                      <ShieldOff size={14} /> Retire
                    </button>
                  )}
                  {c.status === 'retired' && (
                    <button type="button" className="cxr-btn" disabled={busy}
                      onClick={() => review.mutate({ id: c.id, status: 'acknowledged' })}>
                      <RotateCcw size={14} /> Reinstate
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
